<template>
    <wrapper-features-templates-operations
    showFooter
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :footerButtonsParams="buttonsFooterData"
    :tokenParams="{ newPassword: passwordData.newPassword, oldPassword: passwordData.oldPassword }"
    :tokenTitleAppend="t('userArea.definitions.passwordChange.tokenTitleAppend')"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, changePassword)"
    @backOrCloseClicked="navigateByName(routeList.USER_AREA_DEFINITIONS_NAME)">
        <div class="content-wrapper" v-if="!showLoader && !applyErrorState">
            <div class="text-info">{{ t('userArea.definitions.passwordChange.textInfo') }}</div>
            <input-password
            id="oldPassword"
            class="input-info"
            :params="oldPasswordInput"
            :inputValue="passwordData.oldPassword"
            @input="updateDataHandler({ fieldName: 'oldPassword', fieldValue: $event })"
            @onBlur="newPasswordValidation"/>
            <input-password
            id="newPassword"
            class="input-info"
            :params="newPasswordInput"
            :inputValue="passwordData.newPassword"
            @input="updateDataHandler({ fieldName: 'newPassword', fieldValue: $event }, clearFieldError('newPasswordParams'))"
            @onBlur="newPasswordValidation"/>
            <input-password
            id="confirmNewPassword"
            class="input-info"
            :params="confirmNewPasswordInput"
            :inputValue="passwordData.confirmNewPassword"
            @input="updateDataHandler({ fieldName: 'confirmNewPassword', fieldValue: $event }, clearFieldError('confirmNewPasswordParams'))"
            @onBlur="newPasswordValidation"/>
        </div>
    </wrapper-features-templates-operations>
    <!-- Error Feedback -->
    <feedback-toast
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import InputPassword from '@/components/generic/inputs/Password'
import FeedbackToast from '@/components/generic/toasts/Generic'

/* Vue & Assets */
import { ref, computed, watch } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { NUMBERS_ONLY } from '@/assets/constants/masks'
import { atLeastOneIsEmpty } from '@/assets/scripts/objects'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions */
const { modifyPassword } = mapActions(moduleMapper.USER_AREA_DEFINITIONS_PASSWORD_CHANGE)

/* Generic Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const showMandatoryErrors = ref(false)

/*************************************************
*                                                *
*                      Data                      *
*                                                *
*************************************************/
const passwordData = ref({
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: ''
})

const mandatoryList = ref([
    'oldPassword',
    'newPassword',
    'confirmNewPassword'
])

const fieldErrors = ref([])

const updateDataHandler = ({ fieldName, fieldValue }) => {
    passwordData.value[fieldName] = fieldValue
    // clearFieldError(`${fieldName}Params`)
}

const oldPasswordInput = ref({
    labelText: t('userArea.definitions.passwordChange.oldPasswordLabel'),
    placeholder: t('userArea.definitions.passwordChange.placeholderOldPassword'),
    mandatory: true,
    maxLength: 6,
    errorState: false,
    errorMessage: '',
	mask: NUMBERS_ONLY
})

const newPasswordInput = ref({
    labelText: t('userArea.definitions.passwordChange.newPasswordLabel'),
    placeholder: t('userArea.definitions.passwordChange.placeholderNewPassword'),
    mandatory: true,
    maxLength: 6,
    errorState: false,
    errorMessage: '',
	mask: NUMBERS_ONLY
})

const confirmNewPasswordInput = ref({
    labelText: t('userArea.definitions.passwordChange.newPasswordConfirmationLabel'),
    placeholder: t('userArea.definitions.passwordChange.placeholderNewPasswordConfirmation'),
    mandatory: true,
    maxLength: 6,
    errorState: false,
    errorMessage: '',
	mask: NUMBERS_ONLY
})

/*************************************************
*                                                *
*            Change Password Request             *
*                                                *
*************************************************/
const changePassword = async () => {
    showOverlayLoader.value = true
    showFeedbackToast.value = false

    const payload = {
        oldPassword: passwordData.value.oldPassword,
        newPassword: passwordData.value.newPassword
    }

    try {
        await modifyPassword(payload)
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, changePassword)
    }
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
    oldPasswordParams: oldPasswordInput.value,
    newPasswordParams: newPasswordInput.value,
    confirmNewPasswordParams: confirmNewPasswordInput.value
})

const updateFieldErrors = (fieldName, hasError) => {
    if (hasError) {
        if (!fieldErrors.value.includes(fieldName)) fieldErrors.value.push(fieldName)
    } else {
        fieldErrors.value = fieldErrors.value.filter(string => string !== fieldName)
    }
}

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = ''
    allFormFields.value[fieldName].errorState = false
    updateFieldErrors(fieldName.replace('Params', ''), false)
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}

/**********************************************************************
*                                                                     *
*              MANDATORY FIELDS and INPUT VALIDATIONS LOGIC           *
*                                                                     *
***********************************************************************/
const newPasswordValidation = () => {
    // Comparar se a password antiga é igual a nova ou se a antiga é igual à confirmação da nova
    if ((passwordData.value.oldPassword.length && passwordData.value.oldPassword === passwordData.value.newPassword)) {
        // Se for igual põe a true o erro da antiga password
        oldPasswordInput.value.errorState = true
        oldPasswordInput.value.errorMessage = t('userArea.definitions.passwordChange.oldPasswordNonEqual')
        // se for igual na nova password, ativa o erro aqui
        if (passwordData.value.newPassword) {
            newPasswordInput.value.errorState = true
            newPasswordInput.value.errorMessage = t('userArea.definitions.passwordChange.oldPasswordNonEqual')
        }
    }
    if ((passwordData.value.oldPassword.length && passwordData.value.oldPassword === passwordData.value.confirmNewPassword)) {
        oldPasswordInput.value.errorState = true
        oldPasswordInput.value.errorMessage = t('userArea.definitions.passwordChange.oldPasswordNonEqual')
        // se for igual na confirmação da nova, ativa o erro aqui
        if (passwordData.value.confirmNewPassword) {
            confirmNewPasswordInput.value.errorState = true
            confirmNewPasswordInput.value.errorMessage = t('userArea.definitions.passwordChange.oldPasswordNonEqual')
            return
        }
    }
    // se a nova pw deixar de ser igual à antiga, remove os erros
    if (passwordData.value.oldPassword !== passwordData.value.newPassword && passwordData.value.oldPassword !== passwordData.value.confirmNewPassword) {
        /* Normally we wont be removing erros on blur, but since we need to always validate if passwords now match, we will remove if so */
        clearFieldError('newPasswordParams')
        clearFieldError('confirmNewPasswordParams')
    }

    // se a nova password e a confirmação da pw tiverem tamanho, e a nova password for diferente da confirmação, ativa o erro e a mensagem dos dois
    if (passwordData.value.newPassword.length &&
        passwordData.value.confirmNewPassword.length &&
        passwordData.value.newPassword !== passwordData.value.confirmNewPassword) {
        newPasswordInput.value.errorState = true
        newPasswordInput.value.errorMessage = t('userArea.definitions.passwordChange.passwordNonEqual')
        confirmNewPasswordInput.value.errorState = true
        confirmNewPasswordInput.value.errorMessage = t('userArea.definitions.passwordChange.passwordNonEqual')
        updateFieldErrors('newPassword', true)
        updateFieldErrors('confirmNewPassword', true)

        // if we delete the secretCode from the newPassword this will clean the inline error from confirmNewPassword
        if (!passwordData.value.newPassword.length) {
            clearFieldError('confirmNewPasswordParams')
            return
        }
        // if we delete the secretCode from the confirmNewPassword this will clean the inline error from newPassword
        if (!passwordData.value.confirmNewPassword.length) {
            clearFieldError('newPasswordParams')
            return
        }
    }
    // se a password antiga for diferente da nova e se a password antiga for diferente da confirmação da nova, apagar o erro da password antiga
    if (passwordData.value.oldPassword !== passwordData.value.newPassword && passwordData.value.oldPassword !== passwordData.value.confirmNewPassword) {
        clearFieldError('oldPasswordParams')
    }
}

watch(() => showMandatoryErrors.value, (newValue, oldValue) => {
    if (newValue) mandatoryErrors()
    showMandatoryErrors.value = false
})

const mandatoryErrors = () => {
    const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(passwordData.value, mandatoryList.value)

	getElementOffsetTopPosition(mandatoryFieldsEmptyList[0])

    mandatoryFieldsEmptyList.forEach(fieldName => {
        allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        allFormFields.value[`${fieldName}Params`].errorState = true
    })
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
    showOverlayLoader.value = false
    feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
    clearFields()
}

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const buttonsFooterData = computed(() => {
    return [{
        label: t('userArea.definitions.passwordChange.buttonsFooterData.clear'),
        buttonPosition: 'left',
        callbackFunction: () => { clearFields() }
    },
    {
        label: t('userArea.definitions.passwordChange.buttonsFooterData.validate'),
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'arrow-right-full',
        iconPosition: 'right',
        callbackFunction: () => { validateInput() }
    }]
})

const clearFields = () => {
    passwordData.value = {
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    }
    clearFormErrors()
}

const validateInput = () => {
    atLeastOneIsEmpty(passwordData.value, mandatoryList.value) || fieldErrors.value.length ? showMandatoryErrors.value = true : changePassword()
}
</script>

<style lang="scss" scoped>
.content-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 24px;

    .text-info {
        margin-top: 24px;

        @include H5-Regular;
        color: var(--secondary-dark-3);
    }
}
</style>
